/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DetailsRow, IColumn, IDetailsRowProps } from '@fluentui/react/lib/DetailsList';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2, important } from 'src/core/themes/luisStylist';
import { Intent } from 'src/luis/modules/intents/Intent';
import { Pattern } from 'src/luis/modules/patterns/Pattern';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { PatternRow } from 'src/luis/verticals/patternsTable/components/PatternRow';

export type PatternTableProps = TableProps<Pattern> & {
    /**
     * If patterns can be filtered and their intents can be re-assigned.
     */
    areAdvancedOptionsEnabled: boolean;

    /**
     * If a pattern is in edit mode.
     */
    patternIdBeingEdited: React.ReactText;

    /**
     * The intents list.
     */
    intents: Intent[];

    /**
     * The entities list.
     */
    entities: Entity[];

    /**
     * Callback for updating an existing pattern.
     */
    onUpdatePattern: (pattern: Pattern) => boolean | void;

    /**
     * Callback for deleting an existing pattern.
     */
    onDeletePattern: (pattern: Pattern) => boolean | void;

    /**
     * Callback for creating an entity.
     */
    onCreateNewEntity: (name: string) => boolean | void;

    /**
     * Callback for creating a role.
     */
    onCreateNewRole: (entityName: string, roleName: string) => boolean | void;

    /**
     * Callback for creating an entity and a role.
     */
    onCreateNewEntityAndRole: (entityName: string, roleName: string) => boolean | void;

    /**
     * Callback to set the edit mode
     */
    onEdit: (state: boolean) => void;

    /**
     * If a pattern can be edited or deleted
     */
    canPatternBeEditedOrDeleted: boolean;
};

const messages = defineMessages({
    userInputColumnHeader: {
        id: 'patterns.patternsTable.userInputColumnHeader',
        defaultMessage: 'User input'
    },
    intentColumnHeader: {
        id: 'patterns.patternsTable.intentColumnHeader',
        defaultMessage: 'Intent'
    },
    emptyTablePlaceholder: {
        id: 'patterns.patternsTable.emptyTablePlaceholder',
        defaultMessage: "You haven't created any patterns yet."
    }
});

const theme = getScopedTheme('PatternsPage');
const { styleComponent } = getStylistV2('PatternTable');

const Row = styleComponent(DetailsRow)('Row', {
    minHeight: '55px',
    ...important({ background: 'transparent' }),
    $nest: { '&:hover': important({ background: 'transparent' }) }
});

export const CellContainer = styleComponent<IStackProps>(Stack)('CellContainer', () => ({ marginTop: 5 }));

const renderLabeledIntent = (pattern: Pattern, intents: Intent[], onChanged: (selectedIntent: Intent) => void) => {
    const selectedKey = intents.find(i => i.id === pattern.labeledIntent.id);

    return (
        <LuisDropdown
            maxHeight={300}
            ariaLabel={selectedKey.name}
            selectedKey={selectedKey.id}
            onChange={(_, __, index) => onChanged(intents[index])}
            options={intents.map(i => ({ key: i.id, text: i.name }))}
        />
    );
};

export const PatternTable = (props: PatternTableProps) => {
    const {
        items,
        onEdit,
        sortKey,
        intents,
        entities,
        selection,
        onUpdatePattern,
        onDeletePattern,
        onCreateNewRole,
        onCreateNewEntity,
        patternIdBeingEdited,
        onCreateNewEntityAndRole,
        areAdvancedOptionsEnabled,
        canPatternBeEditedOrDeleted,
        ...rest
    } = props;
    const { localize } = useLocalization();

    const columns: IColumn[] = [
        {
            key: 'text',
            name: localize(messages.userInputColumnHeader),
            fieldName: 'text',
            minWidth: 100,
            isResizable: true,
            isMultiline: true,
            isSorted: sortKey && !!(sortKey.key === 'text'),
            isSortedDescending: sortKey && !!(sortKey.key === 'text') && sortKey?.order === 'desc',
            onRender: (pattern: Pattern) => (
                <PatternRow
                    entities={entities}
                    pattern={pattern}
                    onUpdatePattern={onUpdatePattern}
                    onDeletePattern={onDeletePattern}
                    onCreateNewEntity={onCreateNewEntity}
                    onCreateNewRole={onCreateNewRole}
                    onCreateNewEntityAndRole={onCreateNewEntityAndRole}
                    isInEditMode={canPatternBeEditedOrDeleted && patternIdBeingEdited && patternIdBeingEdited === pattern.id}
                    canPatternBeEditedOrDeleted={canPatternBeEditedOrDeleted}
                    onEdit={onEdit}
                />
            )
        },
        {
            key: 'labeledIntentColumn',
            name: localize(messages.intentColumnHeader),
            fieldName: 'intent',
            minWidth: 200,
            isResizable: true,
            isPadded: true,
            onRender: (pattern: Pattern) =>
                areAdvancedOptionsEnabled ? (
                    renderLabeledIntent(pattern, intents, (selectedIntent: Intent) =>
                        onUpdatePattern({ ...pattern, labeledIntent: selectedIntent })
                    )
                ) : (
                    <CellContainer verticalFill>
                        <LuisText color={theme.textColor} data-automation-id="intentName">
                            {pattern.labeledIntent.name}
                        </LuisText>
                    </CellContainer>
                )
        }
    ];

    const onRenderRow = (rowProps: IDetailsRowProps) => <Row {...rowProps} />;

    return (
        <Table<Pattern>
            items={items}
            columns={columns}
            selection={selection}
            onRenderRow={onRenderRow}
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            {...rest}
        />
    );
};
